// Small per-browser UI preferences (open or collapsed panels and sections),
// kept in localStorage and shared live between every component that reads
// the same key. Storage can be missing or refuse writes (private mode): the
// default then applies and the choice lasts for this page only.
import { useCallback, useSyncExternalStore } from "react";

/** The project page's right panel (Pendampingan, Chat di project ini, File project). */
export const SIDE_PANEL_KEY = "procureai.project.sidepanel";
/** The sidebar's Project section. */
export const NAV_PROJECTS_KEY = "procureai.nav.projects";
/** The sidebar's Riwayat section. */
export const NAV_HISTORY_KEY = "procureai.nav.history";

const memory = new Map<string, boolean>();
const listeners = new Set<() => void>();

function read(key: string, fallback: boolean): boolean {
  if (memory.has(key)) return memory.get(key)!;
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(key);
    if (raw === "open") return true;
    if (raw === "closed") return false;
  } catch {
    // No storage: the default applies.
  }
  return fallback;
}

/** Sets a flag for every reader; stored as "open" or "closed". */
export function setFlag(key: string, open: boolean): void {
  memory.set(key, open);
  try {
    localStorage.setItem(key, open ? "open" : "closed");
  } catch {
    // Private mode: this page only.
  }
  for (const l of listeners) l();
}

/** Forgets the in-memory copies (tests start from storage again). */
export function resetFlags(): void {
  memory.clear();
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** An open/closed flag; `fallback` when nothing is stored. */
export function useFlag(key: string, fallback = true): [boolean, (open: boolean) => void, () => void] {
  const open = useSyncExternalStore(
    subscribe,
    () => read(key, fallback),
    () => fallback,
  );
  const set = useCallback((next: boolean) => setFlag(key, next), [key]);
  const toggle = useCallback(() => setFlag(key, !read(key, fallback)), [key, fallback]);
  return [open, set, toggle];
}
